import { Component, Mixins, Prop } from 'vue-property-decorator'
import { mapActions, mapGetters, mapState } from 'vuex'
import merge from 'lodash/merge'
import CustomPrices from '../components/CustomPrices/CustomPrices.vue'
import { NumberMixin, Spinner, UnitSelect, VatRateSelect } from '@kwixeo/interface-vue'
import editorConfig from './editorConfig'

@Component({
  computed: {
    ...mapGetters('user', ['teamConfigs', 'currency']),
    ...mapState('catalog', ['saleCategories']),
    ...mapGetters('subscriptions', ['hasModule']),
    ...mapState('customFields', ['customFields'])
  },
  methods: {
    ...mapActions('catalog', ['loadSaleCategories'])
  },
  components: {
    CustomPrices,
    Spinner,
    UnitSelect,
    VatRateSelect
  }
})
export default class CatalogElementMixin extends Mixins(NumberMixin) {
  // VueX elements
  teamConfigs!: KwixeoInterface.GenericObject
  currency!: KwixeoFramework.Currency
  loadSaleCategories!: (force?: boolean) => void
  saleCategories!: KwixeoCatalog.SaleCategories
  hasModule!: (key: string) => boolean
  customFields!: KwixeoCustomFields.Field[]

  // Child elements
  protected localElement!: KwixeoCatalog.CatalogElementForm
  get totalCosts (): number {
    return 0
  }

  @Prop({ default: null }) value!: KwixeoCatalog.CatalogElement|null
  @Prop({ default: null }) id!: number|null

  protected loading = true

  get status (): KwixeoCatalog.FormStatus[] {
    return [
      { id: false, label: this.$t('catalog.form.status.disable') },
      { id: true, label: this.$t('catalog.form.status.enable') }
    ]
  }

  async mounted (): Promise<void> {
    await this.loadSaleCategories()

    if (this.value) {
      if (this.value.id) {
        this.checkElementDatas(this.value)
          .then(this.setLocalElement)
      } else {
        this.localElement = JSON.parse(JSON.stringify(merge(this.localElement, this.value)))
      }
    }

    if ((this.value && !this.id) || !this.id) {
      this.loading = false
    }
  }

  get grossMarginAmount (): number {
    return +this.number(this.localElement.unit_price_ht) - +this.totalCosts
  }

  get grossMarginRate (): number {
    const costs = this.totalCosts
    if (costs === 0) {
      return 0
    }
    return (this.grossMarginAmount / costs) * 100
  }

  get markupRate (): number {
    const price = this.number(this.localElement.unit_price_ht)
    if (price === 0) {
      return 0
    }

    const rate = (this.grossMarginAmount / price) * 100
    return rate >= 100 ? 99.99 : rate
  }

  get vatRate (): number {
    return this.localElement.vat_rate ? this.number(this.localElement.vat_rate.rate) : 0
  }

  get computedEditorConfig (): KwixeoInterface.GenericObject {
    const customFields: KwixeoInterface.GenericObject = {
      dynamicVariables: {
        variations: {
          label: 'Variations de produit',
          variables: [
            { value: 'VARIATION-LEVEL1', label: 'Valeur de la variation pour le niveau 1' },
            { value: 'VARIATION-LEVEL2', label: 'Valeur de la variation pour le niveau 2' },
            { value: 'VARIATION-LEVEL3', label: 'Valeur de la variation pour le niveau 3' }
          ]
        },
        contracts: {
          label: 'Contrats',
          variables: [
            { value: 'CONTRACT-NUM', label: 'Référence du contrat' },
            { value: 'CONTRACT-TITLE', label: 'Titre du contrat' },
            { value: 'CONTRACT-BEGIN', label: 'Date de début de l\'échéance' },
            { value: 'CONTRACT-END', label: 'Date de fin de l\'échéance' }
          ]
        }
      }
    }

    const fields = this.customFields.filter(field => field.usage.includes('contract'))
    if (fields.length > 0) {
      const variables: { value: string; label: string }[] = []
      fields.forEach(field => {
        variables.push({
          value: 'CUSTOM-FIELD-' + field.id,
          label: field.label
        })
      })

      customFields.dynamicVariables['customFields'] = {
        label: 'Contrats - Champs personnalisés',
        variables
      }
    }

    return merge(
      editorConfig,
      customFields
    )
  }

  // Check if the element has all relationship loaded, override in component
  protected async checkElementDatas (element: KwixeoCatalog.CatalogElement): Promise<KwixeoCatalog.CatalogElement> {
    return element
  }

  protected onNameChanged (): void {
    if (!this.localElement.designation || this.localElement.designation === '') {
      this.localElement.designation = this.localElement.name
    }
  }

  // Apply element property
  protected setLocalElement (element: KwixeoCatalog.CatalogElement): void {
    const formElement = JSON.parse(JSON.stringify(element))
    formElement.formStatus = element.status.active
    this.localElement = formElement
  }

  // update sale price from margin amount
  protected updateMarginAmount (amount: number): void {
    this.localElement.unit_price_ht = this.number(this.totalCosts) + this.number(amount)
    this.calculUnitPriceHT()
  }

  // update sale price from margin rate
  protected updateMarginRate (rate: number): void {
    this.localElement.unit_price_ht = this.number(this.totalCosts) * (1 + (this.number(rate) / 100))
    this.calculUnitPriceHT()
  }

  // update sale price from markup rate
  protected updateMarkupRate (rate: number): void {
    this.localElement.unit_price_ht = this.number(this.totalCosts) / (100 - this.number(rate)) * 100
    this.calculUnitPriceHT()
  }

  // Calculate unit prices from ht
  protected calculUnitPriceHT (): void {
    const val: number = this.number(this.localElement.unit_price_ht)
    const tva = this.roundNumber(val * this.vatRate / 100, 2)
    const ttc = this.roundNumber(tva + val, 2)
    this.localElement.unit_price_tva = tva
    this.localElement.unit_price_ttc = ttc
  }

  // Calculate unit prices from ttc
  protected calculUnitPriceTTC (val: number): void {
    const ht = val / ((this.vatRate / 100) + 1)
    this.localElement.unit_price_tva = this.roundNumber(val - ht, 2)
    this.localElement.unit_price_ht = ht
  }
}
